import { motion, type MotionValue } from "motion/react";
import { BRAND } from "../../lib/brand";

/**
 * The four dashed concentric circles from the design.
 *
 * Source geometry, build/SectionHero.svg (1920×1200): circles centred on
 * (968.5, 600.5) with radii 366.5 / 522.5 / 664 / 869.5. The outer circle is
 * 0.906× the artboard width and 1.449× its height — so it is deliberately
 * cropped top and bottom, and the dashes reach every edge.
 *
 * `ratio` is each radius over the outer one, which is what lets the whole set
 * scale off a single diameter and keep the design's proportions exactly.
 */
const RINGS = [
  { viewBox: 736, r: 366.5, ratio: 0.42151, tiltX: 70, tiltY: 0, spin: 18, dir: 1 },
  { viewBox: 1048, r: 522.5, ratio: 0.60092, tiltX: 50, tiltY: 25, spin: 26, dir: -1 },
  { viewBox: 1331, r: 664, ratio: 0.76366, tiltX: 20, tiltY: -30, spin: 34, dir: 1 },
  { viewBox: 1742, r: 869.5, ratio: 1, tiltX: 60, tiltY: -15, spin: 46, dir: -1 },
] as const;

/** Fixed pixel sizes, used by the orbital (preloader) variant. */
const FIXED_SIZES = [
  "w-[300px] h-[300px] md:w-[520px] md:h-[520px]",
  "w-[430px] h-[430px] md:w-[740px] md:h-[740px]",
  "w-[560px] h-[560px] md:w-[960px] md:h-[960px]",
  "w-[700px] h-[700px] md:w-[1200px] md:h-[1200px]",
];

/**
 * Outer diameter for the flat variant, defined as `--gc-ring-d` on the
 * `.gc-ring-field` class in globals.css so it can respond to breakpoints
 * (phones need a width-driven value, desktop follows the design's ratio).
 */
export const RING_FIELD_DIAMETER = "var(--gc-ring-d)";

/** Radius of ring 2 — the circle the design sits the mascot on. */
export const RING_2_RADIUS = `calc(${RING_FIELD_DIAMETER} * ${RINGS[1].ratio / 2})`;

interface OrbitalRingsProps {
  /** Pointer offset in the range -0.5..0.5, for the parallax tilt. */
  mouse?: { x: number; y: number };
  /** Optional scroll-driven rotations; when given, they replace the idle spin. */
  scrollRotations?: MotionValue<number>[];
  /**
   * "orbital" tilts the rings into 3D orbital planes at fixed pixel sizes
   * (the preloader look). "flat" keeps them as true concentric circles sized
   * to the viewport, as drawn in build/SectionHero.svg.
   */
  variant?: "orbital" | "flat";
  className?: string;
}

export function OrbitalRings({
  mouse = { x: 0, y: 0 },
  scrollRotations,
  variant = "orbital",
  className = "",
}: OrbitalRingsProps) {
  const isFlat = variant === "flat";

  return (
    <div
      // gc-ring-box is the size container the flat rings measure themselves
      // against — see .gc-ring-box / --gc-ring-d in globals.css. It is safe to
      // contain because inset-0 gives it a definite size in both axes.
      className={`pointer-events-none absolute inset-0 overflow-hidden ${isFlat ? "gc-ring-field gc-ring-box" : ""} ${className}`}
      aria-hidden="true"
    >
      {RINGS.map((ring, i) => {
        const scrollRotate = scrollRotations?.[i];
        const size = isFlat ? `calc(${RING_FIELD_DIAMETER} * ${ring.ratio})` : undefined;

        return (
          <motion.div
            key={ring.r}
            className={`absolute left-1/2 top-1/2 ${isFlat ? "" : FIXED_SIZES[i]}`}
            style={{
              ...(isFlat ? { width: size, height: size } : {}),
              x: "-50%",
              y: "-50%",
              // Flat rings keep a whisper of parallax so the section still reacts
              // to the pointer, without turning the circles into ellipses.
              rotateX: isFlat ? mouse.y * 4 : ring.tiltX,
              rotateY: isFlat ? mouse.x * 4 : ring.tiltY + mouse.x * 18,
              rotateZ: isFlat ? 0 : mouse.y * 10,
              ...(scrollRotate ? { rotate: scrollRotate } : {}),
            }}
            {...(scrollRotate
              ? {}
              : {
                  animate: { rotate: 360 * ring.dir },
                  transition: { duration: ring.spin, repeat: Infinity, ease: "linear" },
                })}
          >
            <svg className="block w-full h-full" fill="none" viewBox={`0 0 ${ring.viewBox} ${ring.viewBox}`}>
              <circle
                cx={ring.viewBox / 2}
                cy={ring.viewBox / 2}
                r={ring.r}
                stroke={BRAND.green}
                strokeDasharray="14 14"
                strokeLinecap="round"
                strokeWidth="3"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </motion.div>
        );
      })}
    </div>
  );
}
